import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { HtmlPreview } from "../components/HtmlPreview";
import { ErrorNote, Spinner } from "../components/primitives";
import { api } from "../lib/api";
import { useEventStream } from "../lib/useEventStream";

/**
 * One HTML deliverable at the width of the window — where "Open in new tab"
 * goes.
 *
 * It used to open the file itself, which is the real deliverable and reads
 * the same here: the frame is the file, with its contents, tabs, evidence
 * rail and live charts. What a bare file cannot do is ask for anything — it
 * has no parent to send a finetune through, and serving it with a policy that
 * let it call the API would hand that to everything the file holds. So the
 * tab is the app's, holding the file in the same sandboxed frame as the
 * preview, and Download is the file.
 *
 * Outside the app's frame (`Layout`), because the document is the page; the
 * one line above it says whose it is and leads back.
 */
export function ReadDeliverable() {
  const { runId = "", "*": name = "" } = useParams();
  const run = useQuery({ queryKey: ["run", runId], queryFn: () => api.run(runId) });
  const { events } = useEventStream(runId, true);

  if (run.isLoading) return <Spinner />;
  if (run.isError) {
    return (
      <div className="p-6">
        <ErrorNote error={run.error} />
      </div>
    );
  }

  const target = run.data?.engagement?.target ?? runId;
  return (
    <div className="flex min-h-dvh flex-col bg-surface">
      <header className="flex items-center gap-3 border-b border-line px-4 py-2 text-sm">
        <Link
          to={`/engagements/${encodeURIComponent(runId)}?tab=deliverables`}
          className="text-accent"
        >
          ← {target}
        </Link>
        <span className="min-w-0 truncate text-muted" title={name}>
          {name}
        </span>
      </header>
      <HtmlPreview runId={runId} name={name} events={events} full />
    </div>
  );
}
